import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';

interface ActiveLeaseWarningModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  leaseName?: string;
  leaseStatus?: 'active' | 'terminated';
}

const LeaseStatusWarningModal: React.FC<ActiveLeaseWarningModalProps> = ({
  open,
  onClose,
  onConfirm,
  leaseName,
  leaseStatus = 'active'
}) => {
  const theme = useTheme();
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'warning.main',
          background: theme.palette.mode === 'dark' 
            ? 'rgba(31, 41, 55, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        fontWeight: 600
      }}>
        Edit {leaseStatus === 'active' ? 'Active' : 'Terminated'} Lease
        <IconButton
          onClick={onClose}
          sx={{ ml: 'auto', color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          icon={<WarningIcon />}
        >
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            This lease is currently {leaseStatus === 'active' ? 'active and in use' : 'terminated'}.
          </Typography>
          <Typography variant="body2">
            Any changes you make will overwrite the existing lease data{leaseStatus === 'active' ? ' and could affect the current tenant agreement' : ''}.
          </Typography>
        </Alert>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Lease:</strong> {leaseName || 'Unnamed Lease'}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Before proceeding, please consider:
          </Typography>
          
                      <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The current lease is {leaseStatus === 'active' ? 'active and may be in use by tenants' : 'terminated'}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Changes will overwrite all existing lease data
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Consider creating a new lease instead if this is for a different tenant
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                You can always revert changes by saving as a new draft
              </Typography>
            </Box>
        </Box>
        
        <Box sx={{ 
          p: 2, 
          bgcolor: 'background.paper', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            💡 Recommendation:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you need to make changes to a {leaseStatus === 'active' ? 'active' : 'terminated'} lease, consider creating a new lease or amendment instead of editing the existing one.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          variant="contained"
          color="warning"
          startIcon={<EditIcon />}
          sx={{ 
            minWidth: 120,
            bgcolor: 'warning.main',
            '&:hover': {
              bgcolor: 'warning.dark'
            }
          }}
        >
          Edit Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaseStatusWarningModal; 